import { z } from "zod";

/**
 * Mandatory-channel gate — shared shapes for the MINI-APP side (the API enforces membership of the
 * platform's required channels at the authenticate hook, from the SAME `RequiredChannel` config the
 * bot's `/start` gate uses). A non-member gets 403 `CHANNELS_REQUIRED` whose `details` is a
 * {@link ChannelGateDetails}; the web renders join buttons and re-checks on «عضو شدم».
 */

/**
 * THE gate sentence — one string for every surface that asks a user to join:
 * the bot's `/start` gate screen (`GATE_MESSAGE`), the mini-app gate screen
 * (`CHANNEL_GATE_MESSAGE`) and the `CHANNELS_REQUIRED` 403 body. The channels
 * themselves are the list the colon points at (buttons on both surfaces).
 */
export const MANDATORY_CHANNELS_MESSAGE = "🔓 برای شروع، ابتدا در کانال‌های زیر عضو شوید:";

/**
 * Shown when «عضو شدم» / «بررسی عضویت» was tapped and the server still says no.
 *
 * ONE LINE, on BOTH surfaces (the bot's alert popup and the mini app's gate screen). Both
 * of them are looking at the channel list WHILE they read this — the bot's alert pops over
 * the gate message, the app's notice sits above the join buttons — so re-listing what is
 * left, or re-explaining which button to press, was saying in words what the screen behind
 * it was already saying in buttons.
 */
export const GATE_STILL_MISSING_MESSAGE = "🙃 هنوز عضو همهٔ کانال‌ها نشدی!";

/**
 * Shown when Telegram did not answer the membership question in time and the user is not a
 * recently verified member (card-a5 item 5) — the CLEAR RETRY, never an endless wait. It says what
 * happened (Telegram, not the user) and what to do; it never asks a member to join anything.
 *
 * ONE sentence on every surface: the api's 503 `CHANNEL_CHECK_UNAVAILABLE` body (the mini app shows
 * it verbatim beside its retry button), the game-server's join refusal (matched EXACTLY by the web,
 * a contract between two images) and the bot's reply / alert (≤ 200 characters).
 */
export const CHANNEL_CHECK_UNAVAILABLE_MESSAGE =
  "⏳ تلگرام به‌موقع جواب نداد و عضویتت در کانال‌ها بررسی نشد؛ چند لحظه دیگر دوباره امتحان کن.";

/** One channel the user must join (enough to render a join link). */
export const gateChannelSchema = z.object({
  /** Telegram chat id or `@username` (as configured). */
  chatId: z.string(),
  /** Public @handle (no `@`), when the channel is public. */
  username: z.string().nullable(),
  /** Display label. */
  title: z.string(),
  /** A private invite link, when set (private channels without a public @handle). */
  inviteLink: z.string().nullable(),
  /** The bot-created per-(campaign|direct) invite link to hand THIS user for the join — set by the
   *  gate services (تبلیغات). Optional so an old api response (no joinUrl) still parses. */
  joinUrl: z.string().nullable().optional(),
});
export type GateChannel = z.infer<typeof gateChannelSchema>;

/** The `details` payload on a `CHANNELS_REQUIRED` 403. */
export const channelGateDetailsSchema = z.object({
  channels: z.array(gateChannelSchema),
});
export type ChannelGateDetails = z.infer<typeof channelGateDetailsSchema>;
